// These should be removed once we transition to webpack as it is throwing warnings due to having no import/exports
/* eslint-disable react/jsx-no-undef */
/* eslint-disable no-undef */

import React, { createRef, MouseEvent } from 'react';
import * as ReactRedux from 'react-redux';
import AddHighlightToCurrentDiscussion from 'yamba/add-highlight-to-current-discussion';
import EditableCommentFooter from 'annotator/editable-comment-footer';
import CommentRemovalPane from 'annotator/comment-removal-pane';
import CitationRemovalPane from 'annotator/citation-removal-pane';
import {
  CommentInputAnnotation, CommentCategory, AnnotatorObject
} from 'annotator/annotator-types';
import QAAlertMessage from './bellmere-qa/qa-alert-message';
import QABooleanButtons from './bellmere-qa/qa-boolean-buttons';
import { BellmereQA } from './bellmere-qa/qa-types.d';

declare const Annotator: AnnotatorObject;

type CommentViewPaneProps = {
  annotation: CommentInputAnnotation,
  isEditingDisabled: boolean,
  onEditComment: () => void,
  hasSources: boolean,
  commentCategory: CommentCategory,
  comment: string,
  isEditable: boolean,
  savingSubmission: boolean,
  isCommentFlagged: boolean,
  onCitationDelete: (reason: string) => void,
  timestamp: string,
  onStartRemovingAnnotation: (fromTimestamp: string) => void,
  onCancelRemovingAnnotation: () => void,
  onRemoveComment: (e?: MouseEvent, reason?: string) => void,
  annotationText: string,
  canAddHighlightToCurrentDiscussion?: boolean,
  viewingAsBellmereQa: boolean,
  setBellmereQAResult: (result: BellmereQA.AnnotationResult) => void,
  bellmereQAEditable: boolean,
  result: BellmereQA.AnnotationResult
};

type CommentViewPaneState = {
  removing: boolean,
  removingCitation: boolean
};

class CommentViewPane extends React.Component<CommentViewPaneProps, CommentViewPaneState> {
  state = { removing: false, removingCitation: false };

  node = createRef<HTMLDivElement>();

  componentDidMount(): void {
    $(this.node.current).find('.panel-footer .icon.icon-warning').popover({
      content: Annotator?.bannedWordsPopoverContent,
      html: true,
      placement: 'auto',
      container: 'body',
      trigger: 'click hover focus'
    });
  }

  editClick = (e: MouseEvent): void => {
    e.preventDefault();
    const { isEditingDisabled, onEditComment } = this.props;

    if (!isEditingDisabled) onEditComment();
  };

  removeClick = (e: MouseEvent): void => {
    e.preventDefault();
    const {
      isEditingDisabled,
      hasSources,
      onStartRemovingAnnotation,
      timestamp
    } = this.props;

    this.stopAddingHighlight();

    if (!isEditingDisabled) {
      if (!hasSources) onStartRemovingAnnotation(timestamp);
      this.setState({ removing: true }, () => {
        // Trigger a width check in the comment list when removal pane is shown
        // This ensures the comment-list-delete-reasons-vertical class is applied
        setTimeout(() => {
          const windowWithInstance = window as unknown as { CommentListInstance?: { checkWidth: () => void } };
          if (windowWithInstance.CommentListInstance && windowWithInstance.CommentListInstance.checkWidth) {
            windowWithInstance.CommentListInstance.checkWidth();
          }
        }, 50);
      });
    }
  };

  removeCitationClick = (e: MouseEvent): void => {
    e.preventDefault();
    const { isEditingDisabled } = this.props;
    const { removingCitation } = this.state;

    this.stopAddingHighlight();

    if (!isEditingDisabled) this.setState({ removingCitation: !removingCitation });
  };

  // eslint-disable-next-line class-methods-use-this
  stopAddingHighlight = (): void => {
    // Clear highlight if one is selected and it's not annotated
    Annotator.clearCurrentHighlight();
  };

  stopRemovingAnnotation = (): void => {
    const { onCancelRemovingAnnotation, hasSources } = this.props;

    if (!hasSources) onCancelRemovingAnnotation();
    this.setState({ removing: false, removingCitation: false });
  };

  commentCategory = (): CommentCategory => {
    const { hasSources, commentCategory } = this.props;

    if (hasSources) return CommentCategory.TextMatch;

    return commentCategory != null ? commentCategory.toString() as CommentCategory : CommentCategory.General;
  };

  commentBlank = (): boolean => {
    const { comment } = this.props;

    return comment === null || comment.trim() === '';
  };

  render(): React.JSX.Element {
    const {
      annotation,
      commentCategory,
      isEditable,
      isCommentFlagged,
      isEditingDisabled,
      savingSubmission,
      hasSources,
      onCitationDelete,
      timestamp,
      onRemoveComment,
      canAddHighlightToCurrentDiscussion,
      comment,
      annotationText,
      viewingAsBellmereQa,
      setBellmereQAResult,
      bellmereQAEditable,
      result
    } = this.props;

    const { removing, removingCitation } = this.state;

    const editableFooter = (
      <EditableCommentFooter
        isCommentFlagged={isCommentFlagged}
        isEditingDisabled={isEditingDisabled || savingSubmission}
        hasSources={hasSources}
        removeClick={this.removeClick}
        removeCitationClick={this.removeCitationClick}
        commentBlank={this.commentBlank()}
        editClick={this.editClick}
      />
    );

    const commentRemovalPane = (
      <CommentRemovalPane
        hasSources={hasSources}
        savingSubmission={savingSubmission}
        onRemoveComment={onRemoveComment}
        onCancelRemovingComment={this.stopRemovingAnnotation}
      />
    );

    const citationRemovalPane = (
      <CitationRemovalPane onDelete={onCitationDelete} onCancel={this.stopRemovingAnnotation} />
    );

    const commentPaneClass = Annotator.portFairyEnabled && 'mt-2 mb-2';

    const commentPane = (
      <div className={`panel-body explanation d-flex flex-column gap-2 ${commentPaneClass}`}>
        {!isEditable && commentCategory === CommentCategory.SpellingAndGrammar && !!annotation.correctedSentence && (
          <h5 className="m-0">
            Explanation
          </h5>
        )}

        {viewingAsBellmereQa && (
          <QAAlertMessage message="Is the below explanation quality acceptable?" />
        )}

        {/* eslint-disable-next-line react/no-danger */}
        <span className="comment-container" dangerouslySetInnerHTML={{ __html: comment }} />

        {viewingAsBellmereQa && (
          <div className="text-end">
            <QABooleanButtons
              result={result}
              buttonKey="explanation_quality_acceptable"
              buttonText="yes/no"
              selected={result.explanation_quality_acceptable}
              onUpdate={(r: BellmereQA.AnnotationResult) => setBellmereQAResult(r)}
              readOnly={!bellmereQAEditable}
            />
          </div>
        )}

      </div>
    );

    const Footer = () => {
      if (isEditable) {
        return editableFooter;
      }

      return null;
    };

    return (
      <div className="view-panel" ref={this.node}>
        {!this.commentBlank() && commentPane}
        {canAddHighlightToCurrentDiscussion && (
          <AddHighlightToCurrentDiscussion
            comment={comment}
            annotationText={annotationText}
            timestamp={timestamp}
          />
        )}
        <Footer />
        {removingCitation && citationRemovalPane}
        {removing && commentRemovalPane}
      </div>
    );
  }
}

const mapStateToProps = (state, props) => ({
  addHighlightsToSession: state.questionPanel.visible,

  selectedHighlightInSession:
    (state.classroomSession.inSession || state.isViewingMeeting) &&
    state.questionHighlights.includes(props.timestamp),

  canAddHighlightToCurrentDiscussion: state.classroomSession.active
});

export const YambaCommentViewPane = ReactRedux.connect(mapStateToProps)(CommentViewPane);
export default CommentViewPane;
